import type { components } from "@dolfin/api-admin";

export type ActivityResource = components["schemas"]["ActivityResource"];
export type ActivityResponse = components["schemas"]["ActivityAnalyticsAdminResponse"];
export type ActivityPeriod = components["schemas"]["ActivityPeriod"];
export type ActivityOrganisation = components["schemas"]["ActivityOrganisation"];
export type ActivityOrganisationPeriod = components["schemas"]["ActivityOrganisationPeriod"];
export type ActivityResourceTotal = components["schemas"]["ActivityResourceTotal"];

export type Granularity = "months" | "weeks";

export const RESOURCE_LABELS: Record<ActivityResource, string> = {
  Bills: "Bills",
  Invoices: "Invoices",
  Customers: "Customers",
  Suppliers: "Suppliers",
  Products: "Products",
  TaxRates: "Tax rates",
  PurchaseOrders: "Purchase orders",
  DeliveryNotes: "Delivery notes",
  CreditNotes: "Credit notes",
  SupplierCreditNotes: "Supplier credit notes",
  Expenses: "Expenses",
  Receipts: "Receipts",
};

/** Resources grouped the way an accountant reads them, so twelve tiles scan as four short rows. */
export const RESOURCE_GROUPS: { label: string; resources: ActivityResource[] }[] = [
  { label: "Payables", resources: ["Bills", "PurchaseOrders", "DeliveryNotes", "SupplierCreditNotes", "Suppliers"] },
  { label: "Receivables", resources: ["Invoices", "CreditNotes", "Customers", "Products"] },
  { label: "Spend", resources: ["Expenses", "Receipts"] },
  { label: "Setup", resources: ["TaxRates"] },
];

export const ACTIVE_DEFINITION =
  "An organisation is active in a period if it created at least one bill, invoice, customer, supplier, product, tax rate, purchase order, delivery note, credit note, supplier credit note, expense or receipt in it. Activity does not carry forward - a month with nothing created is not billable.";

// Integers come back as `number | string` in the generated schema.
export function toNumber(value: number | string | undefined | null): number {
  if (value === undefined || value === null) return 0;
  const num = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(num) ? num : 0;
}

/** Parsed by hand so a date-only string is never shifted by the viewer's timezone. */
function utcDate(start: string): Date {
  const [year, month, day] = start.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day ?? 1));
}

export function periodLabel(start: string, granularity: Granularity): string {
  const date = utcDate(start);
  return granularity === "months"
    ? date.toLocaleDateString("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" })
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}

export function periodLabelLong(start: string, granularity: Granularity): string {
  const date = utcDate(start);
  return granularity === "months"
    ? date.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })
    : `Week of ${date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}`;
}

const relative = new Intl.RelativeTimeFormat("en-GB", { numeric: "always" });
const STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "3 days ago"; pair with the absolute time on hover. */
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "Never";
  const seconds = (new Date(iso).getTime() - now) / 1000;
  for (const [unit, size] of STEPS) {
    if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString("en-GB")} ${count === 1 ? one : many}`;
}

const compact = new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 });

/** Heatmap cells: "1.2K" keeps every column the same width. */
export function formatCompact(value: number): string {
  return value < 1000 ? String(value) : compact.format(value);
}
